<template>
  <div v-if="character" class="container">
    <transition name="fade" mode="out-in">
      <div class="details" :key="index">
        <v-button :icon="playing ? 'stop' : 'play'" outline @click="info"/>
        <div>
          <div>
            <img v-for="id in character.stories" v-if="history.includes(id)" :key="id" src="@/assets/images/check_on.png"/>
            <img v-else src="@/assets/images/check_off.png"/>
          </div>
          <h2>{{ character.name }}</h2>
          <h4>{{ character.age }}</h4>
        </div>
      </div>
    </transition>
    <div class="navigation">
      <nav>
        <v-button icon="arrow_left" label="vorige" :disabled="pending" @click="prev"/>
        <v-button icon="walk" :label="`Met ${character.name} wandelen`" primary large @click="select" />
        <v-button icon="arrow_right" label="volgende" :disabled="pending" @click="next"/>
      </nav>
    </div>
    <v-button class="exit" icon="check" label="Klaar met wandelen" primary large @click="stop" />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import AudioPlayer from '@/AudioPlayer'
import Button from '@/components/Button'
import { defer } from '@/utils'
import characters from '@/data/characters'

const AUDIO_EVENTS = [ 'play', 'pause', 'loadstart' ]

const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1)

export default {
  name: 'SelectCharacterView',
  components: {
    'v-button': Button
  },
  data () {
    return {
      index: -1,
      pending: false,
      playing: false
    }
  },
  computed: {
    ...mapState([
      'history'
    ]),
    character () {
      return characters[this.index] || {}
    }
  },
  methods: {
    ...mapActions([
      'loadScene',
      'updateScene',
      'selectCharacter'
    ]),
    prev () {
      if (this.pending) return
      this.index = (this.index - 1 + characters.length) % characters.length
      defer(200, () => this.audio.play())
      this.updateScene({ action: 'prev' })
    },
    next () {
      if (this.pending) return
      this.index = (this.index + 1) % characters.length
      defer(200, () => this.audio.play())
      this.updateScene({ action: 'next' })
    },
    info () {
      if (this.playing) {
        AudioPlayer.pause().reset()
      } else {
        AudioPlayer.play()
      }
    },
    select () {
      this.track('select_character', {
        event_category: 'character',
        event_label: capitalize(this.character.name)
      })
      defer(100, () => {
        this.loadScene({ name: 'SelectStory' })
      })
    },
    stop () {
      this.track('end_session', {
        event_category: 'session'
      })
      defer(100, () => {
        this.loadScene({ name: 'End' })
      })
    },
    onAudioChanged (e) {
      this.playing = (e.type === 'play')
    },
    onKeyPress (e) {
      if (e.keyCode === 37) { this.prev() }
      if (e.keyCode === 39) { this.next() }
    }
  },
  watch: {
    index () {
      this.pending = true
      defer(1000, () => { this.pending = false })
    },
    character ({ name }) {
      this.selectCharacter(name)
      AudioPlayer.load(`audio/characters/${name}/about.mp3`)
      defer(2400, () => {
        if (this.character.name === name) {
          AudioPlayer.play()
        }
      })
    }
  },
  created () {
    AUDIO_EVENTS.forEach(e => AudioPlayer.on(e, this.onAudioChanged))
    window.addEventListener('keydown', this.onKeyPress)
  },
  mounted () {
    const name = this.$store.state.character
    this.index = name ? characters.findIndex(char => char.name === name) : 0
    this.audio = new Audio('audio/ui/slide.mp3')
  },
  beforeDestroy () {
    AudioPlayer.pause()
    AUDIO_EVENTS.forEach(e => AudioPlayer.off(e, this.onAudioChanged))
    window.removeEventListener('keydown', this.onKeyPress)
  }
}
</script>

<style scoped lang="scss">
.container {
  position: relative;
  display: flex;
  width: 100%;
  height: 100%;
  padding: 3rem;
  align-items: center;
  justify-content: center;
  @include fade-in(400ms, 2s);
}

.details {
  display: flex;
  justify-content: flex-end;
  width: 54rem;
  height: 60vh;
  transition: opacity 300ms;

  &.fade-enter-active,
  &.fade-leave-active {
    opacity: 0;
  }

  img {
    width: 2.4rem;
    margin: 0.3rem;
  }

  h2 {
    margin: 0;
    line-height: 1.2;
    text-transform: capitalize;
  }

  h4 {
    font-weight: 500;
    font-style: italic;
    padding-right: 0.6rem;
  }

  button {
    margin: 0.6rem 1.2rem;
  }
}

.navigation {
  position: absolute;
  bottom: 3rem;
  width: 100%;
  max-width: 32rem;
  text-align: center;

  nav {
    display: flex;
    align-items: center;
    height: 9rem;
    margin-bottom: 1.5rem;
    justify-content: space-between;
  }
}

.exit {
  position: absolute;
  bottom: 3rem;
  right: 3rem;
}
</style>
